import { differenceInDays, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';
import type { FormSchema } from '../zod';

interface GroupPricingCalculations {
	totalRevenue: number;
	totalRoomNights: number;
	netRevenue: number;
	profitPerRoom: number;
	suggestedRate: number;
	numberOfNights: number;
	isValidDateRange: boolean;
}

/**
 * Custom hook that performs real-time calculations as form values change
 */
export function useGroupPricingCalculations(
	form: UseFormReturn<z.infer<typeof FormSchema>>,
): GroupPricingCalculations {
	// Watch all the fields we need for calculations
	const formValues = useWatch({
		control: form.control,
	});

	// Calculate the number of nights between check-in and check-out
	const numberOfNights = useMemo(() => {
		if (!formValues.check_in || !formValues.check_out) return 0;

		try {
			const checkIn = parseISO(formValues.check_in);
			const checkOut = parseISO(formValues.check_out);

			if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime()))
				return 0;

			const nights = differenceInDays(checkOut, checkIn);
			return Math.max(0, nights);
		} catch (error) {
			console.error('Error calculating number of nights:', error);
			return 0;
		}
	}, [formValues.check_in, formValues.check_out]);

	// Check if the date range is valid
	const isValidDateRange = useMemo(() => {
		if (!formValues.check_in || !formValues.check_out) return false;

		try {
			const checkIn = parseISO(formValues.check_in);
			const checkOut = parseISO(formValues.check_out);

			if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime()))
				return false;

			return checkOut >= checkIn;
		} catch (error) {
			console.error('Error validating date range:', error);
			return false;
		}
	}, [formValues.check_in, formValues.check_out]);

	// Calculate the total room nights
	const totalRoomNights = useMemo(() => {
		if (!numberOfNights || !formValues.rooms_requested) return 0;
		return numberOfNights * formValues.rooms_requested;
	}, [numberOfNights, formValues.rooms_requested]);

	// Calculate the total revenue
	const totalRevenue = useMemo(() => {
		if (!totalRoomNights || !formValues.rate_requested) return 0;
		return totalRoomNights * formValues.rate_requested;
	}, [totalRoomNights, formValues.rate_requested]);

	// Calculate net revenue after commission
	const netRevenue = useMemo(() => {
		if (!totalRevenue) return 0;
		const commissionRate = (formValues.commission || 0) / 100;
		return totalRevenue * (1 - commissionRate);
	}, [totalRevenue, formValues.commission]);

	// Calculate profit per room
	const profitPerRoom = useMemo(() => {
		if (!netRevenue || !totalRoomNights) return 0;

		// Factor in operational expenses and additional revenue
		const directCosts = (formValues.direct_var_opex || 0) * totalRoomNights;
		const additionalRevenue =
			(formValues.f_and_b_profit || 0) +
			(formValues.meeting_space_benefit || 0);

		const profit = netRevenue - directCosts + additionalRevenue;
		return profit / totalRoomNights;
	}, [
		netRevenue,
		totalRoomNights,
		formValues.direct_var_opex,
		formValues.f_and_b_profit,
		formValues.meeting_space_benefit,
	]);

	// Calculate a suggested rate based on profit margin goals
	const suggestedRate = useMemo(() => {
		if (!formValues.profit_margin || !formValues.direct_var_opex) return 0;

		const targetProfitPerRoom = formValues.profit_margin;
		const costs = formValues.direct_var_opex;
		const commissionRate = (formValues.commission || 0) / 100;

		// Rate = (Target Profit + Costs) / (1 - Commission Rate)
		const suggestedRate = (targetProfitPerRoom + costs) / (1 - commissionRate);
		return Math.round(suggestedRate * 100) / 100; // Round to 2 decimal places
	}, [
		formValues.profit_margin,
		formValues.direct_var_opex,
		formValues.commission,
	]);

	return {
		totalRevenue,
		totalRoomNights,
		netRevenue,
		profitPerRoom,
		suggestedRate,
		numberOfNights,
		isValidDateRange,
	};
}
